import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { handleDonate } from "../../../utils/Donation";
import CheckoutReminderModal from "./CheckoutReminderModal";

// Incomplete checkout → reminder popup on Back (same flow as the NGO campaign page).
// When the donor closes the Razorpay checkout without paying, the donation box stays on the page
// and we add one history entry for this same page. The next Back press lands on it (popstate) and
// shows the reminder popup instead of leaving. It shows once per closed checkout: closing it,
// setting a reminder or "leave anyway" lets Back work normally again.
//
// Usage: const reminder = useCheckoutReminder(campaign);
//        <TempleDonationBox onCheckoutAbandoned={reminder.onCheckoutAbandoned} … />
//        {reminder.modal}
// `campaign` feeds the popup's card (title, image, raised / goal); on /temples it's a stand-in.
const useCheckoutReminder = (campaign, source = "temple_checkout_close") => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [abandoned, setAbandoned] = useState(null);
  const [open, setOpen] = useState(false);
  const backGuardArmed = useRef(false);

  const onCheckoutAbandoned = useCallback((details) => {
    setAbandoned(details);
    if (!backGuardArmed.current) {
      window.history.pushState({ ...(window.history.state || {}), checkoutReminderGuard: true }, "", window.location.href);
      backGuardArmed.current = true;
    }
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (!backGuardArmed.current) return;
      backGuardArmed.current = false;
      setOpen(true);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const leaveAnyway = () => {
    setOpen(false);
    window.history.back();
  };

  // "Complete My Donation" / "Donate Now Instead": reopen the same checkout (same donor details,
  // once / monthly), at the amount chosen in the popup.
  const completeDonation = async ({ amount, tipAmount }) => {
    if (!abandoned) return;
    // Close the popup first: its focus trap would block typing inside the Razorpay window
    setOpen(false);
    const retry = { ...abandoned, amount, tipAmount };
    await handleDonate(
      abandoned.form,
      Number(amount),
      navigate,
      { ...abandoned.additionalData, ...(abandoned.frequency === "once" && { tipAmount: tipAmount || 0 }) },
      user?.id,
      null,
      { onDismiss: () => onCheckoutAbandoned(retry) }
    );
  };

  const modal = (
    <CheckoutReminderModal
      open={open}
      campaign={campaign}
      abandoned={abandoned}
      source={source}
      onClose={() => setOpen(false)}
      onLeave={leaveAnyway}
      onCompleteDonation={completeDonation}
    />
  );

  return { onCheckoutAbandoned, modal };
};

export default useCheckoutReminder;
